/**
 * Creative Vendor Registry
 *
 * Central registry for managing creative vendors (Connected Stories, future vendors, etc.).
 * Provides a unified API to interact with whichever vendor is currently active.
 *
 * CACHING STRATEGY:
 * -----------------
 * Uses "lazy optimistic" caching:
 * - If no vendor is found, we keep searching on each call (vendor may appear later)
 * - Once a vendor is found, it's cached (stable, no more DOM queries)
 * - This handles the case where vendor iframe loads after initial check
 *
 * ADDING A NEW VENDOR:
 * --------------------
 * 1. Create a vendor class implementing the vendor interface:
 *    - isAvailable(): boolean
 *    - handleVpaidStart(videoPlayer: HTMLVideoElement): boolean
 *    - getProgress(fallbackProgress: number): number
 * 2. Add it to the `vendors` array below
 *
 * The registry will automatically use the first available vendor.
 * Order in the array determines priority if multiple vendors are available.
 */

import { ConnectedStoriesVendor } from './connected-stories.vendor';

interface Vendor {
  isAvailable(): boolean;
  handleVpaidStart(videoPlayer: HTMLVideoElement): boolean;
  getProgress(fallbackProgress: number): number;
}

// List of all known vendors (order matters for priority)
const vendors: Vendor[] = [
  ConnectedStoriesVendor
  // Add future vendors here
];

// Cache for the active vendor (lazy optimistic strategy)
// - undefined: not yet searched
// - null: searched but not found (will search again)
// - vendor: found and cached (stable)
let cachedVendor: Vendor | null = null;

export class CreativeVendorRegistry {
  /**
   * Returns the first available vendor, or null if none.
   * Uses lazy optimistic caching: caches only when a vendor is found.
   */
  static getActiveVendor(): Vendor | null {
    // If we have a cached vendor, return it (stable)
    if (cachedVendor) {
      return cachedVendor;
    }

    // Search for an available vendor
    const vendor = vendors.find(v => v.isAvailable()) || null;

    // Cache ONLY if we found a vendor
    // If null, we'll search again on next call (vendor may appear later)
    if (vendor) {
      cachedVendor = vendor;
    }

    return vendor;
  }

  /**
   * Checks if any vendor is currently active.
   */
  static hasActiveVendor(): boolean {
    return this.getActiveVendor() !== null;
  }

  /**
   * Lets the active vendor handle VPAID start.
   * Each vendor implements its own logic (e.g., syncing video events).
   *
   * @param videoPlayer - Our video player element
   * @returns true if a vendor handled the start, false otherwise
   */
  static handleVpaidStart(videoPlayer: HTMLVideoElement): boolean {
    const vendor = this.getActiveVendor();
    if (vendor && vendor.handleVpaidStart) {
      return vendor.handleVpaidStart(videoPlayer);
    }
    return false;
  }

  /**
   * Gets the video progress, potentially overridden by the active vendor.
   * Vendors may have their own video player with different progress.
   *
   * @param fallbackProgress - Progress from our video element (used if no vendor)
   * @returns Progress value (from vendor if active, otherwise fallback)
   */
  static getProgress(fallbackProgress: number): number {
    const vendor = this.getActiveVendor();
    if (vendor && vendor.getProgress) {
      return vendor.getProgress(fallbackProgress);
    }
    return fallbackProgress;
  }

  /**
   * Resets the vendor cache. Useful for testing or between ads.
   */
  static resetCache(): void {
    cachedVendor = null;
  }
}
